import { Component, Input, OnInit } from '@angular/core';
import {
  INCOME_FORM_HOUSE_PROPERTY_RENT_KEYS,
  INCOME_FORM_OTHER_PROPERTY_RENT_KEYS,
  IncomeFormRentKey,
  PROPERTY_DETAILS_LABELS,
  PROPERTY_TYPE_LABELS,
} from '../../../../constants/income-from-rent-constants';

interface HousePropertyPairRow {
  residentialTitle: string;
  residentialKey: string;
  commercialTitle: string;
  commercialKey: string;
}

@Component({
    selector: 'app-income-from-rent-en-v2',
    templateUrl: './income-from-rent-en-v2.component.html',
    styleUrls: [
        './income-from-rent-en-v2.component.css',
        '../../../shared/return-view-text-fields.css',
    ],
    standalone: false
})
export class IncomeFromRentEnV2Component implements OnInit {
  @Input() returnData: any;
  @Input() printActive: boolean = false;

  incomeFormRentData: any[] = [];

  private readonly housePropertyAmountParticulars = INCOME_FORM_HOUSE_PROPERTY_RENT_KEYS
    .filter((k) => k.type === 'amount')
    .slice()
    .sort((a, b) => a.order - b.order);

  private readonly otherPropertyAmountParticulars = INCOME_FORM_OTHER_PROPERTY_RENT_KEYS
    .filter((k) => k.type === 'amount')
    .slice()
    .sort((a, b) => a.order - b.order);

  private readonly housePropertyTextParticulars = INCOME_FORM_HOUSE_PROPERTY_RENT_KEYS
    .filter((k) => k.type === 'text')
    .slice()
    .sort((a, b) => a.order - b.order);

  private readonly otherPropertyTextParticulars = INCOME_FORM_OTHER_PROPERTY_RENT_KEYS
    .filter((k) => k.type === 'text')
    .slice()
    .sort((a, b) => a.order - b.order);

  private readonly housePropertyPairRows: HousePropertyPairRow[] = [
    {
      residentialTitle: 'Area Rented Out',
      residentialKey: 'occupiedAreaResidential',
      commercialTitle: 'Area Rented Out',
      commercialKey: 'occupiedAreaCommercial',
    },
    {
      residentialTitle: 'Rental Period (Months)',
      residentialKey: 'rentalPeriodResidentialOrOther',
      commercialTitle: 'Rental Period (Months)',
      commercialKey: 'rentalPeriodCommercial',
    },
    {
      residentialTitle: 'Annual Rent',
      residentialKey: 'annualResidentialOrOther',
      commercialTitle: 'Annual Rent',
      commercialKey: 'annualCommercial',
    },
    {
      residentialTitle: 'Rent Received',
      residentialKey: 'rentResidentialOrOther',
      commercialTitle: 'Rent Received',
      commercialKey: 'rentCommercial',
    },
    {
      residentialTitle: 'Any Charge Paid by Tenant',
      residentialKey: 'advanceResidentialOrOther',
      commercialTitle: 'Any Charge Paid by Tenant',
      commercialKey: 'advanceCommercial',
    },
    {
      residentialTitle: 'Less: Vacancy Allowance',
      residentialKey: 'vacancyAllowanceResidentialOther',
      commercialTitle: 'Less: Vacancy Allowance',
      commercialKey: 'vacancyAllowanceCommercial',
    },
    {
      residentialTitle: 'Total Rent',
      residentialKey: 'totalRentResidential',
      commercialTitle: 'Total Rent',
      commercialKey: 'totalRentCommercial',
    },
  ];

  private readonly housePropertyPairKeys = new Set(
    this.housePropertyPairRows.flatMap((row) => [row.residentialKey, row.commercialKey])
  );

  private readonly sqFtKeys = new Set([
    'selfOccupiedArea',
    'occupiedAreaResidential',
    'occupiedAreaCommercial',
    'totalArea',
  ]);

  private readonly yesNoKeys = new Set(['anyCC', 'ownershipRadio']);

  readonly propertyTypeKey = 'propertyType';
  readonly shareOfOwnershipKey = 'shareOfOwnership';
  readonly otherOwnersNameKey = 'otherOwnersName';

  constructor() { }

  getValue(item: any, key: string): any {
    return item ? item[key] : null;
  }

  getPropertyTypeTitle(propertyType: any): string {
    const v = propertyType === null || propertyType === undefined ? '' : String(propertyType).trim();
    return PROPERTY_TYPE_LABELS[v] || v;
  }

  getPropertyDetailsTitle(propertyDetails: any): string {
    const v = propertyDetails === null || propertyDetails === undefined ? '' : String(propertyDetails).trim();
    return PROPERTY_DETAILS_LABELS[v] || v;
  }

  isHouseProperty(item: any): boolean {
    return this.getValue(item, this.propertyTypeKey) === 'House_Property';
  }

  isOtherProperty(item: any): boolean {
    return this.getValue(item, this.propertyTypeKey) === 'Others_Rent';
  }

  isSoleOwner(item: any): boolean {
    return item?.ownershipRadio === true || item?.ownershipRadio === '1';
  }

  hasValue(item: any, key: string): boolean {
    const val = this.getValue(item, key);
    return val !== null && val !== undefined && val !== '';
  }

  /** API stores inverted: true = tenant did not pay (No), false = tenant paid (Yes). */
  private formatServiceChargePaid(value: any): string {
    if (value === true || value === 1 || value === '1' || value === 'true') {
      return 'No';
    }
    if (value === false || value === 0 || value === '0' || value === 'false') {
      return 'Yes';
    }
    return String(value ?? '');
  }

  private formatYesNo(value: any): string {
    if (value === true || value === 1 || value === '1' || value === 'true' || value === 'Yes' || value === 'YES') {
      return 'Yes';
    }
    if (value === false || value === 0 || value === '0' || value === 'false' || value === 'No' || value === 'NO') {
      return 'No';
    }
    return String(value ?? '');
  }

  getTextParticulars(item: any): IncomeFormRentKey[] {
    const fields = this.isHouseProperty(item)
      ? this.housePropertyTextParticulars
      : this.otherPropertyTextParticulars;

    return fields.filter((field) => {
      if (field.key === this.shareOfOwnershipKey || field.key === this.otherOwnersNameKey) {
        return !this.isSoleOwner(item) && this.hasValue(item, field.key);
      }
      return this.hasValue(item, field.key);
    });
  }

  getTextFieldTitle(field: IncomeFormRentKey): string {
    return this.stripSqFtFromTitle(field.title);
  }

  getTextFieldDisplayValue(item: any, field: IncomeFormRentKey): string {
    const value = this.getValue(item, field.key);
    if (field.key === this.propertyTypeKey) {
      return this.getPropertyTypeTitle(value);
    }
    if (field.key === 'propertyDetails') {
      return this.getPropertyDetailsTitle(value);
    }
    if (field.key === 'serviceChargePaid') {
      return this.formatServiceChargePaid(value);
    }
    if (this.yesNoKeys.has(field.key)) {
      return this.formatYesNo(value);
    }
    return this.formatSqFtValue(field.key, value);
  }

  isSqFtKey(key: string): boolean {
    return this.sqFtKeys.has(key);
  }

  getPairCellDisplayValue(item: any, key: string): string {
    if (!this.hasValue(item, key)) {
      return '';
    }
    return this.formatSqFtValue(key, this.getValue(item, key));
  }

  private stripSqFtFromTitle(title: string): string {
    return String(title || '')
      .replace(/\s*\((?:in\s+)?Sq\.?\s*ft\.?\)\s*/gi, '')
      .trim();
  }

  private formatSqFtValue(key: string, value: any): string {
    const display = String(value ?? '');
    if (!this.sqFtKeys.has(key) || display === '') {
      return display;
    }
    return `${display} Sq. ft.`;
  }

  getHousePropertyPairRows(item: any): HousePropertyPairRow[] {
    return this.housePropertyPairRows.filter((row) =>
      this.hasValue(item, row.residentialKey) || this.hasValue(item, row.commercialKey)
    );
  }

  getHousePropertyOtherParticulars(item: any): IncomeFormRentKey[] {
    return this.housePropertyAmountParticulars.filter((particular) =>
      !this.housePropertyPairKeys.has(particular.key)
      && particular.group !== 'specialRentalIncome'
      && this.hasValue(item, particular.key)
    );
  }

  getOtherPropertyParticulars(item: any): IncomeFormRentKey[] {
    return this.otherPropertyAmountParticulars.filter((particular) =>
      particular.group !== 'specialRentalIncome'
      && this.hasValue(item, particular.key)
    );
  }

  getSpecialRentalIncomeParticulars = (item: any): IncomeFormRentKey[] => {
    const fields = this.isHouseProperty(item)
      ? this.housePropertyAmountParticulars
      : this.otherPropertyAmountParticulars;
    return fields.filter((particular) =>
      particular.group === 'specialRentalIncome' && this.hasValue(item, particular.key)
    );
  };

  getTotalSpecialRentalIncome = (item: any): number => {
    const specialRows = this.getSpecialRentalIncomeParticulars(item);
    return specialRows.reduce((sum, row) => {
      const n = Number(String(this.getValue(item, row.key) ?? 0).replace(/,/g, ''));
      return sum + (isNaN(n) ? 0 : n);
    }, 0);
  };

  getFinalIncomeParticulars(item: any): IncomeFormRentKey[] {
    const fields = this.isHouseProperty(item)
      ? this.housePropertyAmountParticulars
      : this.otherPropertyAmountParticulars;
    return fields.filter((particular) =>
      particular.key === 'finalTotal' && this.hasValue(item, particular.key)
    );
  }

  ngOnInit(): void {
    const rentData = this.returnData?.incomeData?.rentData;
    this.incomeFormRentData = Array.isArray(rentData) ? rentData : [];
  }
}
